import './styles/root/app.scss';
import { Navigate, Route, useLocation, useNavigate } from 'react-router-dom';
import { Routes } from 'react-router-dom';
import {
  lazy,
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import history from './history';
import { fetchAppInit } from './lib/api/app-init';
import { fetchIdentity } from './lib/api/identity/identity-api';
import { restoreSession } from './lib/api/session/restore-session';
import { applySessionFromResponse } from './lib/api/session/apply-session';
import {
  setEmployeeIdentity,
  setAgencySessionResolving,
} from './store/reducers/authSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from './store/store';
import {
  setOnboardingOAuthCallbackParams,
  setPreUserInvitation,
  setReferral,
  setUserCompanyDetails,
  updateIntercom,
  updateProfile,
} from './store/reducers/userSlice';
import { AnimatePresence } from 'framer-motion';
import Message from './components/generic/Message';
import PageNotFound from './pages/pageNotFound/PageNotFound';
import LogoutRoute from './components/routers/LogoutRoute';
import EmailVerificationSuccess from './pages/redirects/EmailVerificationSuccess';
import VerifyEmail from './pages/redirects/VerifyEmail';
import StripeSubscriptionSuccess from './pages/redirects/StripeSubscriptionSuccess';
import StripeSubscriptionFailed from './pages/redirects/StripeSubscriptionFailed';
import i18n from './internationalization/i18n';
import AdUpPayOnboardingSuccess from './pages/redirects/AdUpPayOnboardingSuccess';
import OnboardingCompleteBanner from './ui/banners/OnboardingCompleteBanner';
import appThemeEngine from './lib/helper-functions/appThemeEngine';
import moment from 'moment';
import { tokenRevokeAtLogOutUser } from './lib/api/logout-user';
import { setMessage } from './store/reducers/appSlice';
import { useTranslation } from 'react-i18next';
import PluginAuthPage from './pages/plugin/PluginAuthPage';
import OauthConsent from './pages/oauthConsent/OauthConsent';
import { consumePendingMcpOAuth } from './lib/mcp-oauth/pending';
import WhiteLabel from './pages/whiteLabel/WhiteLabel';
import {
  setAutoSelectAgencyRole,
  setIsOnboardingCompleteBannerOpen,
  setIsPaymentPopupOpen,
  setIsPaymentPopupFromBrandLimit,
  setIsSwitchShopPopupOpen,
  setIsWhiteLabel,
  setIsHelpSetupPopupOpen,
  setIsChatAnimTriggered,
} from './store/reducers/uiSlice';
import Unsubscribe from './pages/redirects/Unsubscribe';
import SettingsPage from './pages/settings/SettingsPage';
import React from 'react';
import PortalLogin from './pages/login-via-portal/PortalLogin';
import CheckoutTrackingPage from './pages/checkoutTracking/CheckoutTrackingPage';
import Dashboard from './pages/dashboard/Dashboard';
import HeaderMain from './components/header/HeaderMain';
import { runCacheBuster } from './lib/cache-buster/cache-bust-handler';
import {
  APP_ENV,
  CACHE_BUSTER_EXECUTION_INTERVAL,
  GOOGLE_OAUTH_CLIENT_ID,
  INTERCOM_APP_ID,
  INTERCOM_ENABLED_ENVS,
  REDUX_STORE_RESYNC_INTERVAL,
} from './lib/constants/central-api';
import isNotEmptyString from './lib/helper-functions/isNotEmptyString';
import {
  AGENCY_SHELL_THEME,
  bootstrapAgencyShell,
  isAgencyOnlySession,
} from './lib/api/agency-shell-init';
import { allRoutes, HOME_ROUTE } from './lib/constants/route';
import RecommendationsSubscriptionStatus from './pages/redirects/RecommendationsSubscriptionStatus';
import { GoogleOAuthProvider } from '@react-oauth/google';
import localStorageHardReset from './lib/helper-functions/localStorageHardReset';
import get_access_token_by_integration_key_api_call from './lib/api/ai-tracker/get_access_token_by_integration_key_api_call';
import SplashScreenNew from './components/splash-screen/SplashScreenNew';
import Popup from './ui/popup/Popup';
import get_tara_packages_api_call from './lib/api/tara/get_tara_packages_api_call';
// Phase 6-C — `Recommendations` page removed (legacy cleanup).
import Notifications from './pages/Notifications/Notifications';
// W3 — MCP Control Center (govern AI tool access per brand). It is the SINGLE
// hub, hosted at /integrations (the whitelisted OAuth redirect URL); the old
// standalone Integrations page is retired and /control-center redirects here.
import ControlCenter from './pages/controlCenter/ControlCenter';
import Skills from './pages/skills/Skills';
import LegacySkillsRedirect from './components/routers/LegacySkillsRedirect';
import PromptSchedules from './pages/prompt-schedules/PromptSchedules';
// Phase 13 — per-employee Claude Code plugin (MCP) setup page.
import McpSetup from './pages/mcpSetup/McpSetup';
// Phase 13 — Team management page lives at the top level (was /settings/team).
import TeamPage from './pages/team/TeamPage';
import SwitchShop from './components/sidebar/SwitchShop';
import AIOptimizations from './pages/AIOptimizations/AIOptimizations';
import ActionsPage from './pages/actions/ActionsPage';
import ClientsPage from './pages/clients/ClientsPage';
import ClientReportsPage from './pages/client-reports/ClientReportsPage';
import {
  setCurrentChatInfo,
  setLastUsedMCPs,
} from './store/reducers/chatSlice';
import SubscriptionPackages from './components/SubscriptionPackages/SubscriptionPackages';
import { requestPreUserInvitation } from './lib/api/pre-user-invitation';
import taraChatAnimationEngine from './lib/helper-functions/taraChatAnimationEngine';
import MainAuthWrapper from './onboardingFlow/MainAuthWrapper';
import Onboarding from './onboardingFlow/onboarding/Onboarding';
import AgencyPackageItem from './components/SubscriptionPackages/agency/AgencyPackageItem';
import { tara_agency_pricing_hierarchy } from './lib/constants/tara_pricing_plans';
// Phase 8-G — Agency Stats survives as a deep-link from the legacy
// dashboard's "Detailed stats" header. The parallel `/agency/*` portal
// was retired in the FE consolidation wave; this is the only `/agency/*`
// route still wired up.
import AgencyStats from './pages/stats/Stats';
// W5 — audit-log viewer (owner/team_lead). Top-level page at `/audit-log`,
// wrapped in the same agency auth pattern as `/stats`.
// Unified Log Center (`/logs`) — hosts the Audit + Meta API activity tabs.
// The standalone AuditLog / MetaActivity pages are now rendered (in embedded
// mode) by LogCenter, and their old routes redirect into it.
import LogCenter from './pages/logCenter/LogCenter';
// Phase 9-D — public email approve/deny landing page (no auth wrapper).
import EmailApprovalLanding from './pages/emailApproval/EmailApprovalLanding';
import { isDemoMode } from './lib/demo/demo-mode';
import { isNewDemoMode } from './lib/demo-new/demo-mode';
import { normaliseLanguage, rememberLanguage } from './lib/i18n/languages';
import { syncLanguageFromUserSettings } from './lib/i18n/sync-language';
import { usePlanLock } from './lib/plan-lock/usePlanLock';
import {
  isRouteOpenWhileLocked,
  selectIsPlanLockToast,
  SUBSCRIPTION_PAGE_ROUTE,
} from './lib/plan-lock/plan-lock-rules';
import PlanLockScreen from './components/planLock/PlanLockScreen';
// Phase 6-C — Client Portal (white-label magic-link)
import {
  ClientPortalMagicLink,
  ClientPortalOtpLogin,
  ClientPortalDashboard,
  ClientPortalReports,
  ClientPortalExpired,
  ClientPortalProtectedRoute,
} from './pages/client-portal';
// Super-admin portal moved out of tara-client — it now lives in the Adup Panel
// app. The admin data/API stays in central-api (/api/v1/admin/*).

// Public interactive demo — a fully-simulated, explorable copy of the REAL
// portal (src/lib/demo + src/pages/demo). Lazy so the demo code stays out of
// the main bundle for normal users.
const DemoLauncher = lazy(() => import('./pages/demo/DemoLauncher'));
const DemoShell = lazy(() => import('./pages/demo/DemoShell'));
// Demo 2.0 — the NEW demo at /demo-new (forked; the /demo above is frozen for
// clients who already hold its link).
const DemoNewLauncher = lazy(() => import('./pages/demo-new/DemoLauncher'));
const DemoNewShell = lazy(() => import('./pages/demo-new/DemoShell'));
const DemoAgentsPage = lazy(
  () => import('./pages/demo-new/agents/DemoAgentsPage')
);
const DemoClassicAgentsPage = lazy(
  () => import('./pages/demo/agents/DemoAgentsPage')
);
// Public client one-pager (no auth, no backend) — lazy for the same reason.
const OnePager = lazy(() => import('./pages/onePager/OnePager'));

//* Splash timings. The short delay only smooths the paint flicker once the page
//* has loaded; the max wait is the hard ceiling so a stalled `window.load`
//* (third-party tag that never resolves) can never pin the splash on screen.
const APP_LOADING_FLICKER_GUARD_MS = 1000;
const APP_LOADING_MAX_WAIT_MS = 4000;
//* Ceiling on the cookie-restore boot chain (`GET /session` + `/me`) before the
//* splash gives way to the route guards.
const AGENCY_SESSION_RESOLVE_MAX_WAIT_MS = 12000;

function App() {
  const { t } = useTranslation();
  const location = useLocation();
  const currentRoute = location.pathname;
  const queryParameters = new URLSearchParams(window.location.search);

  const [isSidebarDrawerOpen, setIsSidebarDrawerOpen] =
    useState<boolean>(false);
  const [isShopSwitching, setIsShopSwitching] = useState<boolean>(false);
  const currentChat = useSelector((state: RootState) => state.chat.currentChat);
  const isPaymentPopupOpen = useSelector(
    (state: RootState) => state.UI.isPaymentPopupOpen
  );
  const isPaymentPopupFromBrandLimit = useSelector(
    (state: RootState) => state.UI.isPaymentPopupFromBrandLimit
  );
  const isSwitchShopPopupOpen = useSelector(
    (state: RootState) => state.UI.isSwitchShopPopupOpen
  );
  const isHelpSetupPopupOpen = useSelector(
    (state: RootState) => state.UI.isHelpSetupPopupOpen
  );
  const hasSkippedHelpSetup = useSelector(
    (state: RootState) => state.UI.hasSkippedHelpSetup
  );

  const toggleSidebarDrawer = () => {
    setIsSidebarDrawerOpen(!isSidebarDrawerOpen);
  };
  const togglePaymentPopupModal = () => {
    const newState = !isPaymentPopupOpen;
    dispatch(setIsPaymentPopupOpen(newState));
    // Show change plan view only if opened from brand limit AND has active subscription
    if (newState) {
      setShowChangePlanView(
        isPaymentPopupFromBrandLimit && !!taraSubscriptionDetails
      );
    } else {
      setShowChangePlanView(false);
      // Reset the brand limit flag when closing
      dispatch(setIsPaymentPopupFromBrandLimit(false));
    }
  };
  const toggleSwitchShopPopupModal = () => {
    if (isShopSwitching) return; //? Prevent closing popup when switching shop
    dispatch(setIsSwitchShopPopupOpen(!isSwitchShopPopupOpen));
  };

  /* get onboarding data */
  const staticURL = useSelector((state: RootState) => state.app.staticURL);
  const onboardingObject = useSelector(
    (state: RootState) => state.user.onboarding.onboarding_object
  );
  const userProfile = useSelector((state: RootState) => state.user.profile);
  const merchantTheme = useSelector(
    (state: RootState) => state.user?.merchant?.config
  );
  const intercomData = useSelector((state: RootState) => state.user.intercom);
  const accessToken = useSelector(
    (state: RootState) => state.auth.authorization.accessToken
  );
  const isAdminSession = useSelector(
    (state: RootState) => state.auth.authorization?.isAdminSession ?? false
  );
  const message = useSelector((state: RootState) => state.app.message);
  // ----- Plan lock -----
  // An organisation without an active plan or trial may only reach the
  // subscription page (when the member can buy a plan) or a screen that sends
  // them to their account owner. Every other signed-in route is closed.
  const planLock = usePlanLock();
  const isRouteClosedByPlanLock =
    planLock.locked &&
    !isRouteOpenWhileLocked(currentRoute, planLock.canManageBilling);
  // The refusal that locked the app is explained by the lock itself; pages
  // that were loading when it arrived must not each toast it as an error.
  const isPlanLockToast = useSelector((state: RootState) =>
    selectIsPlanLockToast(state, state.app.message?.message)
  );
  const isInitSuccess = useSelector(
    (state: RootState) => state.app.isInitSuccess
  );
  const appLanguage = useSelector((state: RootState) => state.app.language);
  const isAppThemeDark = useSelector((state: RootState) => state.UI.theme.dark);
  const appThemeType = useSelector((state: RootState) => state.UI.theme.type);
  const {
    isTaraChatAnimationEnabled,
    isSidebarMinimized,
    isChatAnimTriggered,
  } = useSelector((s: RootState) => s.UI);
  const isWhiteLabel = useSelector((state: RootState) => state.UI.isWhiteLabel);
  const isOnboardingCompleteBannerOpen = useSelector(
    (state: RootState) => state.UI.isOnboardingCompleteBannerOpen
  );
  const lastVersionCheckTimestamp = useSelector(
    (state: RootState) => state.app.lastVersionCheckTimestamp ?? 0
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ----- Phase 11 identity bootstrap -----
  // Login stores the agency Bearer token (`setAgencyAccessToken`) but never
  // fetched the employee identity, so `employeeIdentity` stayed null on a
  // normal login. Every shop/brand selector on the agency pages reads its
  // options from `employeeIdentity.accessible_shops` / `accessible_brands`, so
  // they rendered empty ("shop select doesn't work everywhere"). This effect
  // closes the gap: whenever the agency token is present but the identity has
  // not loaded yet, fetch `/me` and populate it. Keyed on the token, so it
  // covers every login path (they all dispatch `setAgencyAccessToken`).
  const agencyAccessToken = useSelector(
    (state: RootState) => state.auth?.agency?.accessToken
  );
  const employeeIdentity = useSelector(
    (state: RootState) => state.auth?.agency?.employeeIdentity
  );
  const agencySessionResolving = useSelector(
    (state: RootState) => state.auth?.agency?.sessionResolving ?? false
  );
  useEffect(() => {
    if (agencyAccessToken && !employeeIdentity) {
      fetchIdentity(agencyAccessToken)
        .then((identity) => {
          dispatch(setEmployeeIdentity(identity));
        })
        .catch((err) => {
          // Non-fatal: the page still renders; selectors stay empty until the
          // next attempt. Logged for triage rather than surfaced to the user.
          // eslint-disable-next-line no-console
          console.error('[identity bootstrap] fetchIdentity failed', err);
        });
    }
  }, [agencyAccessToken, employeeIdentity, dispatch]);

  // ----- MCP-client OAuth consent return-to (TARA_PLUGIN_OAUTH2_PLAN.md WS3) -----
  // The gateway 302s a signed-out browser to /oauth/authorize; the consent
  // page parks the request in sessionStorage and sends the visitor to /login.
  // As soon as the agency identity resolves (any login path, or the "Not
  // you?" re-login), bring them straight back to finish the consent.
  useEffect(() => {
    if (!employeeIdentity) {
      return;
    }
    const pending = consumePendingMcpOAuth();
    if (pending) {
      navigate(`/oauth/authorize?${pending}`, { replace: true });
    }
  }, [employeeIdentity, navigate]);

  // ----- Hotfix 2026-08-06 — brand list for invite-only employee sessions -----
  // `user.profile.all_shops` is only ever hydrated by seller-init, which needs
  // a seller JWT. An INVITED employee (agency/employees/login) never holds one,
  // so every all_shops consumer (ClientsList, SwitchShop, BrandQuickSwitch,
  // client-reports) rendered empty even though /api/v1/me returned their
  // assigned brands. Mirror the /me scope into all_shops for these sessions.
  // Seller/white-label sessions are untouched — seller-init stays the owner.
  useEffect(() => {
    if (accessToken?.sellerToken || isWhiteLabel) {
      return;
    }
    const shops = employeeIdentity?.accessible_shops ?? [];
    if (shops.length === 0) {
      return;
    }
    const idBySlug = new Map<string, string>(
      (employeeIdentity?.accessible_brands ?? []).map((b: any) => [
        b.brand_slug as string,
        String(b.brand_id ?? ''),
      ])
    );
    const hydrated = shops
      .filter((s: any) => Boolean(s.slug))
      .map((s: any) => ({
        id: idBySlug.get(s.slug) ?? '',
        slug: s.slug as string,
        name: (s.name ?? s.slug) as string,
        webshop_url: '',
      }));
    if (hydrated.length === 0) {
      return;
    }
    const currentSlugs = (userProfile?.all_shops ?? [])
      .map((s) => s.slug)
      .join('|');
    // 2026-09-07 — `profile.role` is ALSO seller-init-only (defaults to
    // 'seller'), and the bottom-left brand switcher (SidebarTara), SwitchShop,
    // ClientsList and ProfileDropdown all key on `role === 'agency'`. Without
    // it an invite-only employee had the brands mirrored but no switcher to
    // pick one. Every employee session is an agency session by definition.
    const roleInSync = userProfile?.role === 'agency';
    if (currentSlugs === hydrated.map((s) => s.slug).join('|') && roleInSync) {
      return; // already in sync — avoid a dispatch loop
    }
    const activeStillValid =
      isNotEmptyString(userProfile?.active_shop_slug) &&
      hydrated.some((s) => s.slug === userProfile.active_shop_slug);
    dispatch(
      updateProfile({
        role: 'agency',
        all_shops: hydrated,
        active_shop_slug: activeStillValid
          ? userProfile.active_shop_slug
          : hydrated[0].slug,
      })
    );
  }, [
    accessToken?.sellerToken,
    isWhiteLabel,
    employeeIdentity,
    userProfile?.all_shops,
    userProfile?.active_shop_slug,
    userProfile?.role,
    dispatch,
  ]);

  // ----- 2026-09-07 — `user.company` for agency-only sessions -----
  // The legacy `tara/mcp/*` platform calls (Integrations Hub platform grid,
  // "platforms connected" tile, MCP setup) read the brand from
  // `user.company.shop_slug`, which only seller-init writes. An invite-only
  // employee therefore saw every platform as "Not connected" and "—" for the
  // count until they happened to switch brands. Keep `company` pointed at the
  // active brand for these sessions; seller sessions are untouched.
  const companyShopSlugForMirror = useSelector(
    (state: RootState) => state.user?.company?.shop_slug
  );
  useEffect(() => {
    if (accessToken?.sellerToken || isWhiteLabel || !agencyAccessToken) {
      return;
    }
    const activeSlug = userProfile?.active_shop_slug;
    if (
      !isNotEmptyString(activeSlug) ||
      companyShopSlugForMirror === activeSlug
    ) {
      return;
    }
    const active = (userProfile?.all_shops ?? []).find(
      (s) => s.slug === activeSlug
    );
    if (!active) {
      return;
    }
    dispatch(
      setUserCompanyDetails({
        name: active.name,
        shop_id: active.id ? String(active.id) : undefined,
        shop_slug: active.slug,
        webshop_url: active.webshop_url,
      })
    );
  }, [
    accessToken?.sellerToken,
    isWhiteLabel,
    agencyAccessToken,
    userProfile?.active_shop_slug,
    userProfile?.all_shops,
    companyShopSlugForMirror,
    dispatch,
  ]);

  // ----- 2026-09-07 — app SHELL for agency-only sessions -----
  // `UI.theme.type` (which sidebar renders) and `app.staticURL` are only set
  // by the seller-authenticated init, which these sessions never run. They
  // were stuck on the legacy theme-1 shell: no Brands / Reporting entries, no
  // brand switcher, broken avatar. Bootstrap the shell from the
  // unauthenticated seller-init payload once per session.
  const uiThemeType = useSelector((state: RootState) => state.UI?.theme?.type);
  useEffect(() => {
    if (
      !isAgencyOnlySession(
        agencyAccessToken,
        accessToken?.sellerToken,
        isWhiteLabel
      )
    ) {
      return;
    }
    if (uiThemeType === AGENCY_SHELL_THEME && isNotEmptyString(staticURL)) {
      return; // already on the agency shell
    }
    bootstrapAgencyShell();
  }, [
    agencyAccessToken,
    accessToken?.sellerToken,
    isWhiteLabel,
    uiThemeType,
    staticURL,
  ]);

  // ----- Phase B — cookie-backed FULL session restore (the "stay logged in" fix) --
  // On every cold boot the in-memory tokens are gone (the auth slice is never
  // persisted), but the httpOnly `tara_session` cookie set at login may still
  // be valid. Restore the WHOLE session from it once on mount: GET /session
  // (cookie-authed) re-mints the login payload (fresh seller JWT + agency key +
  // user). Restoring the seller token is what keeps the legacy seller-JWT calls
  // (tara/mcp/*, seller-init, dashboard data) working after reload — restoring
  // only the agency identity (the old /me-only restore) left those 401-ing and
  // the dashboard empty. `sessionResolving` gates the splash until the full
  // session + identity are in place (no /login flash, no empty-then-filled
  // flicker). A 401 (no/expired cookie) leaves tokens empty and the token-expiry
  // effect / route guards fall through to /login exactly as before.
  useEffect(() => {
    let cancelled = false;
    restoreSession()
      .then(async (data) => {
        if (cancelled || !data) {
          return;
        }
        // Restore the agency identity first (using the re-minted key) so the
        // tokens + identity land together — the agencyAccessToken-keyed effect
        // above then sees `employeeIdentity` already set and won't re-fetch.
        let identity = null;
        try {
          identity = await fetchIdentity(data.agency_api_key);
        } catch {
          // Non-fatal: the agencyAccessToken-keyed effect retries /me.
        }
        if (cancelled) {
          return;
        }
        const hasSellerToken = applySessionFromResponse(data);
        if (identity) {
          dispatch(setEmployeeIdentity(identity));
        }
        // Seller token is now in memory → seller-init + the rest authenticate.
        // An INVITED employee has no seller account, so the restore comes back
        // agency-only; calling seller-init without a seller token 401s and fires
        // the "session expired" logout on every reload.
        if (hasSellerToken) {
          fetchAppInit();
        }
      })
      .catch(() => {
        // No valid cookie/session — guards redirect once resolving clears.
      })
      .finally(() => {
        if (!cancelled) dispatch(setAgencySessionResolving(false));
      });
    //? Watchdog — `sessionResolving` holds the splash on screen, and the chain
    //? above also awaits `fetchIdentity`, which has no timeout of its own. If
    //? anything in it never settles, clear the flag anyway and let the route
    //? guards decide; a stuck splash is worse than an extra /login bounce.
    const resolveWatchdog = window.setTimeout(() => {
      if (!cancelled) dispatch(setAgencySessionResolving(false));
    }, AGENCY_SESSION_RESOLVE_MAX_WAIT_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(resolveWatchdog);
    };
    // Mount-once: the cookie is only read at boot. Post-login restore (when a
    // token arrives after mount) is handled by the token-keyed effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [taraSubscriptionPackages, setTaraSubscriptionPackages] = useState<
    any[]
  >([]);
  const [showChangePlanView, setShowChangePlanView] = useState<boolean>(false);

  const is_ai_onboarding_inprogress = useSelector(
    (state: RootState) => state.user?.ai_onboarding?.is_ai_onboarding_inprogress
  );
  const is_ai_onboarding_complete = useSelector(
    (state: RootState) => state.user?.ai_onboarding?.is_ai_onboarding_completed
  );
  const is_tracker_classes_configured = useSelector(
    (state: RootState) => state.user?.stats?.is_tracker_classes_configured
  );
  const ai_tracker_api_key = useSelector(
    (state: RootState) => state.user?.stats?.ai_tracker_api_key
  );
  const is_tracker_access_token = useSelector(
    (state: RootState) => state.user?.stats?.ai_tracker_access_token
  );
  const ai_tracker_access_token_expires_in = useSelector(
    (state: RootState) => state.user?.stats?.ai_tracker_access_token_expires_in
  );
  const shopSlug = useSelector(
    (state: RootState) => state.user.company?.shop_slug
  );
  const availableTaraSubscriptionPackages = useSelector(
    (state: RootState) => state.user?.shop?.availableTaraSubscriptionPackages
  );
  const taraSubscriptionDetails: any = useSelector(
    (state: RootState) => state.user?.shop?.taraSubscriptionDetails
  );

  // Update showChangePlanView when subscription details change or popup opens from brand limit
  useEffect(() => {
    if (isPaymentPopupOpen) {
      // Only show change plan view if opened from brand limit AND has active subscription
      setShowChangePlanView(
        isPaymentPopupFromBrandLimit && !!taraSubscriptionDetails
      );
    }
  }, [
    taraSubscriptionDetails,
    isPaymentPopupOpen,
    isPaymentPopupFromBrandLimit,
  ]);

  const is_ai_onboarding_completed = useSelector(
    (state: RootState) => state.user?.ai_onboarding?.is_ai_onboarding_completed
  );
  const isTrackingScriptConnectionSuccess = useSelector(
    (state: RootState) =>
      state.user?.isTrackingScriptConnectionSuccess ?? undefined
  );

  const [statistics, setStatistics] = useState<any>(null);

  const [appLoading, setAppLoading] = useState<boolean>(true);

  // useEffect(() => {
  //   // On initial app load, we need to fetch statistics and integration data.
  //   // On every page load for the dashboard route ('/dashboard'), we need to fetch statistics.

  //   const fetchStatistics = async () => {
  //     if (
  //       accessToken?.sellerToken &&
  //       shopSlug &&
  //       (currentRoute === '/dashboard' ||
  //         currentRoute !== '/recommendations' ||
  //         currentRoute === undefined) // Check if we are on the dashboard or initial load
  //     ) {
  //       try {
  //         const res = await get_statistics_api_call();
  //         get_available_tracking_data_api_call();
  //         setStatistics(res); // Set the fetched statistics to state
  //       } catch (error) {
  //         console.error('Error fetching statistics:', error);
  //       }
  //     }
  //   };

  //   fetchStatistics();
  // }, [accessToken?.sellerToken, shopSlug, currentRoute]);

  // useEffect(() => {
  //   //* Fetch Tracking Script connection status at initial user login
  //   if (
  //     (!isNotEmptyString(isTrackingScriptConnectionSuccess) ||
  //       currentRoute === '/integrations') &&
  //     isNotEmptyString(accessToken?.sellerToken) &&
  //     isNotEmptyString(shopSlug) &&
  //     currentRoute !== '/dashboard' &&
  //     currentRoute !== '/recommendations'
  //   ) {
  //     get_available_tracking_data_api_call(true); //checkScriptConnection
  //   }
  // }, [accessToken?.sellerToken, shopSlug, currentRoute]);

  const handlePreUserInvitation = async (invitationId: string) => {
    try {
      const response = await requestPreUserInvitation(invitationId);
      const userData = response?.data?.user_data;
      if (response && response?.success && userData) {
        dispatch(setPreUserInvitation({ id: invitationId, data: userData }));
      }
    } catch (error) {
      console.error('Error handling pre-user invitation:', error);
    }
  };

  useEffect(() => {
    // A locked organisation is refused this product call; don't repeat it on
    // every route change.
    if (planLock.locked) {
      return;
    }
    if (
      ai_tracker_api_key &&
      typeof ai_tracker_api_key === 'string' &&
      is_tracker_access_token === ''
    ) {
      get_access_token_by_integration_key_api_call(ai_tracker_api_key);
    } else if (
      ai_tracker_access_token_expires_in &&
      moment(ai_tracker_access_token_expires_in) < moment()
    ) {
      get_access_token_by_integration_key_api_call(ai_tracker_api_key);
    }
  }, [
    ai_tracker_api_key,
    is_tracker_access_token,
    ai_tracker_access_token_expires_in,
    currentRoute,
    planLock.locked,
  ]);

  const taraSubscriptionPackagesAPICall = async () => {
    try {
      const shouldSyncPackages = () => {
        if (
          availableTaraSubscriptionPackages?.syncTS &&
          availableTaraSubscriptionPackages?.structuredPackageData
        ) {
          const currentTime: number = new Date().getTime();
          const timeDiff: number =
            currentTime - availableTaraSubscriptionPackages?.syncTS;

          //? Check if the last sync was less than REDUX_STORE_RESYNC_INTERVAL 👇🏼
          if (timeDiff < REDUX_STORE_RESYNC_INTERVAL) {
            return false;
          }
        }
        return true;
      };

      const structuredPackageData = shouldSyncPackages()
        ? await get_tara_packages_api_call()
        : availableTaraSubscriptionPackages?.structuredPackageData;

      setTaraSubscriptionPackages(structuredPackageData);
    } catch (error: any) {
      console.error('Error fetching tara packages:', error);
    }
  };

  useLayoutEffect(() => {
    // only when we're on onboarding
    if (!window.location.pathname.startsWith('/onboarding')) return;

    const search = window.location.search; // e.g. "?code=...&state=..."
    if (!search) return;

    const p = new URLSearchParams(search);
    const code = p.get('code') || p.get('auth_code');
    const state = p.get('state');

    if (code && state) {
      // store the whole query so rerenders/redirects don't drop it
      dispatch(setOnboardingOAuthCallbackParams(search));
      // clean the URL (optional)
      window.history.replaceState({}, '', window.location.pathname);
      console.log('[OAuth][App] captured for onboarding');
    }
  }, [dispatch, location.pathname]);

  useEffect(() => {
    if (
      accessToken?.sellerToken &&
      !is_ai_onboarding_inprogress &&
      shopSlug &&
      isInitSuccess &&
      !appLoading
    ) {
      taraSubscriptionPackagesAPICall();
    }
  }, [
    accessToken?.sellerToken,
    shopSlug,
    is_ai_onboarding_inprogress,
    isInitSuccess,
    appLoading,
  ]);

  useEffect(() => {
    if (accessToken?.sellerToken) {
      const shouldRedirect =
        currentRoute !== '/register' &&
        currentRoute !== '/login-via-portal' &&
        currentRoute !== '/verify-email' &&
        !isAdminSession;

      //* Commented below for the MCP Launch
      // if (
      //   shouldRedirect &&
      //   !is_ai_onboarding_complete &&
      //   !is_tracker_classes_configured
      // ) {
      //   navigate('/onboarding', { replace: true });
      // } else if (shouldRedirect && is_ai_onboarding_inprogress) {
      //   navigate('/onboarding', { replace: true });
      // }
      //* Added below for the MCP Launch
      // A locked organisation is held on the subscription page; sending it
      // back to onboarding from there would bounce between the two.
      if (shouldRedirect && is_ai_onboarding_inprogress && !planLock.locked) {
        navigate('/onboarding', { replace: true });
      }
    }
  }, [
    currentRoute,
    planLock.locked,
    isAdminSession,
    accessToken?.sellerToken,
    is_ai_onboarding_inprogress,
    is_ai_onboarding_complete,
    is_tracker_classes_configured,
  ]);

  useEffect(() => {
    if (currentRoute === '/tara') {
      dispatch(
        setCurrentChatInfo({
          chatTokenCount: 0,
        })
      );
      dispatch(setLastUsedMCPs([]));
    }
  }, [currentRoute]);

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(setIsOnboardingCompleteBannerOpen(false));
      clearTimeout(timer);
    }, 4000);
  }, [isOnboardingCompleteBannerOpen, dispatch]);

  useLayoutEffect(() => {
    const unlisten = history.listen(({ location, action }) => {
      let unblock = history.block((tx) => {
        // Navigation was blocked! Let's show a confirmation dialog
        // so the user can decide if they actually want to navigate
        // away and discard changes they've made in the current page.
        let url = tx.location.pathname;
        if (window.confirm(`Are you sure you want to go to ${url}?`)) {
          // Unblock the navigation.
          unblock();

          // Retry the transition.
          tx.retry();
        }
      });
    });

    return unlisten;
  }, []);

  //* Changing app language (only English and Dutch are applied; anything
  //* else falls back to English) and remembering it for the next page load
  useEffect(() => {
    const language = normaliseLanguage(appLanguage);
    i18n.changeLanguage(language);
    rememberLanguage(language);
  }, [appLanguage]);

  //* Apply the language saved under Settings > General once the session is up
  useEffect(() => {
    if (isInitSuccess) {
      syncLanguageFromUserSettings();
    }
  }, [isInitSuccess]);

  //* Set auto select agency role based on URL params
  useEffect(() => {
    const accType = queryParameters.get('acctype') as string;
    if (accType && accType === 'agency') {
      dispatch(setAutoSelectAgencyRole(true));
    } else if (accType && (accType === 'seller' || accType === 'individual')) {
      dispatch(setAutoSelectAgencyRole(false));
    }
  }, [currentRoute, queryParameters]);

  //* Fetch app init
  useEffect(() => {
    if (currentRoute !== '/authorize') {
      dispatch(setIsWhiteLabel(false));
    }
    if (
      currentRoute !== '/authorize' &&
      currentRoute !== '/login-via-portal' &&
      !currentRoute.startsWith('/agency') && // `/agency/stats` survivor — uses agency Bearer
      !currentRoute.startsWith('/email-approve') && // Phase 9-D — public email approval landing
      !currentRoute.startsWith('/demo') && // public interactive demo funnel (no auth)
      !currentRoute.startsWith('/one-pager') && // public client one-pager (no auth)
      !currentRoute.startsWith('/client-portal') && // Phase 6-C — magic-link auth
      !currentRoute.startsWith('/admin') // Phase 6-C — admin portal uses its own auth
    ) {
      // Phase B — skip the seller-init call when there is no seller credential
      // to authenticate it. On a cold reload the in-memory JWT is gone, so an
      // unconditional call here would 401 and fire the "session expired" logout
      // + toast on EVERY reload — even when a valid agency cookie is about to
      // restore the portal. Fresh logins still call fetchAppInit() directly
      // (Login.tsx / sign-up), so seller data loads there as before.
      const bootCacheBust = queryParameters.get('cacheBustToken');
      const hasSellerCredential =
        isNotEmptyString(accessToken?.sellerToken) ||
        isNotEmptyString(accessToken?.currentWhiteLabelSellerToken) ||
        isNotEmptyString(bootCacheBust ?? '');
      if (hasSellerCredential) {
        fetchAppInit();
      }
    }
  }, []);

  //* Extracting cacheBustToken from URL if there is any
  const cacheBustToken = queryParameters.get(
    'cacheBustToken'
  ) as unknown as string;

  //* Phase B — does in-memory state represent a seller session whose init is
  //* worth blocking the UI on? On a cold reload this is false (tokens are not
  //* persisted), which lets the splash resolve to the route guards instead of
  //* waiting on `isInitSuccess` that will never flip without a token.
  const hasSellerSession =
    isNotEmptyString(accessToken?.sellerToken) ||
    isNotEmptyString(accessToken?.currentWhiteLabelSellerToken) ||
    isNotEmptyString(cacheBustToken);

  //* Set referral & preUserInvite data via URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const referralParam = params.get('referral');
    const preUserInviteParam = params.get('preUserInvite');

    if (referralParam && typeof referralParam === 'string') {
      dispatch(setReferral(referralParam));
    }

    if (preUserInviteParam && typeof preUserInviteParam === 'string') {
      handlePreUserInvitation(preUserInviteParam);
    }
  }, []);

  //* Clean up cache bust token from URL after handling
  useEffect(() => {
    if (isNotEmptyString(cacheBustToken)) {
      const url = new URL(window.location.href);
      url.searchParams.delete('cacheBustToken');
      window.history.replaceState({}, '', url.toString());
      console.log('🧹 Cache bust token cleaned from URL');
    }
  }, [cacheBustToken]);

  //* Show help setup popup when user lands on HOME_ROUTE or integrations without contact number
  useEffect(() => {
    if (
      isInitSuccess &&
      !appLoading &&
      (currentRoute === HOME_ROUTE || currentRoute === '/integrations') &&
      !hasSkippedHelpSetup &&
      (!userProfile.phone || userProfile.phone.trim() === '')
    ) {
      dispatch(setIsHelpSetupPopupOpen(true));
    }
  }, [
    isInitSuccess,
    appLoading,
    currentRoute,
    hasSkippedHelpSetup,
    userProfile.phone,
  ]);

  //* ******************** Run Cache Buster (FE version check) at given interval --- START ******************** *//

  useEffect(() => {
    const interval = setInterval(() => {
      const currentTime: number = new Date().getTime();
      const hoursDifference: number =
        (currentTime - lastVersionCheckTimestamp) / (1000 * 60 * 60);

      //? Check if the last version check was more than 12 hours ago 👇🏼
      if (isInitSuccess && hoursDifference >= 12) {
        console.log(
          '🔄 CacheBuster : (FE version check) 12 hour interval reached ⏰'
        );
        runCacheBuster();
      }
    }, CACHE_BUSTER_EXECUTION_INTERVAL);

    return () => clearInterval(interval);
  }, [isInitSuccess]);
  //* ******************** Run Cache Buster (FE version check) at given interval --- END ******************** *//

  /* --------------Token expiry check Start---------------- */
  useEffect(() => {
    if (!isNotEmptyString(cacheBustToken)) {
      console.log(
        '⏰ 🚧 Current access token will be expired on :',
        accessToken?.expiresOn
      );
      if (
        currentRoute !== '/authorize' &&
        currentRoute !== '/oauth/authorize' && // MCP-client consent: parks the request + sends to /login itself
        currentRoute !== '/forgot-password' &&
        currentRoute !== '/reset-password' &&
        currentRoute !== '/login-via-portal' &&
        currentRoute !== '/forgot-password' &&
        currentRoute !== '/reset-password' &&
        currentRoute !== '/register' &&
        currentRoute !== '/login' &&
        !currentRoute.startsWith('/agency') && // `/agency/stats` survivor — uses agency Bearer
        !currentRoute.startsWith('/email-approve') && // Phase 9-D — public landing
        !currentRoute.startsWith('/demo') && // public interactive demo funnel (no auth)
        !currentRoute.startsWith('/one-pager') && // public client one-pager (no auth)
        !currentRoute.startsWith('/client-portal') && // Phase 6-C
        !currentRoute.startsWith('/admin') // Phase 6-C
      ) {
        if (accessToken?.expiresOn && accessToken?.expiresOn !== null) {
          if (moment(accessToken?.expiresOn) < moment()) {
            console.log('❌ Access token expired. Logging Out user');
            logoutHandler();
            dispatch(
              setMessage({
                message: t('SessionExpired'),
                messageType: 'error',
              })
            );
          } else {
            console.log('✅ Access token still active');
          }
        } else if (!agencySessionResolving && !employeeIdentity) {
          // No seller-token expiry in memory. On a cold reload this is ALWAYS
          // the case — but a cookie-backed agency session may still be
          // restoring (`agencySessionResolving`) or already restored
          // (`employeeIdentity`). Logging out here would revoke the httpOnly
          // `tara_session` cookie (logout hits LOGOUT_API → AgencySessionCookie
          // ::forget) and bounce to /login, defeating reload-persistence. Only
          // log out when there is genuinely no agency session to restore.
          logoutHandler();
        }
      }
    } else {
      //* Handle cache bust scenario - if we have cacheBustToken but no valid token, redirect to login
      if (!accessToken?.sellerToken && currentRoute === '/') {
        console.log(
          '🔄 Cache bust detected with no valid token, redirecting to login'
        );
        navigate('/login', { replace: true });
      }
    }
  }, [
    accessToken?.expiresOn,
    cacheBustToken,
    currentRoute,
    agencySessionResolving,
    employeeIdentity,
  ]);
  /* --------------Token expiry check End---------------- */

  /* --------------App Theme Settings Start---------------- */
  useEffect(() => {
    appThemeEngine(isAppThemeDark, merchantTheme, appThemeType);
  }, [isAppThemeDark, merchantTheme]);
  /* --------------App Theme Settings End------------------ */

  /* --------------Setting Favicon Start---------------- */
  useEffect(() => {
    const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement;

    if (staticURL && merchantTheme?.merchant_favicon) {
      link.href = `${staticURL}/medium/${merchantTheme.merchant_favicon}`;
      console.log('has merchant favicon');
    } else {
      link.href = '/favicon.ico'; // Assuming the favicon is located in the public folder
      console.log('no merchant favicon');
    }
  }, [staticURL, merchantTheme]);
  /* --------------Setting Favicon End------------------ */

  /* -------------run intercom at app load--------------- */
  useEffect(() => {
    if (
      // @ts-ignore:next-line
      window.Intercom &&
      INTERCOM_ENABLED_ENVS.includes(APP_ENV)
    ) {
      const appId = intercomData.app_id || INTERCOM_APP_ID;

      if (!intercomData.isFirstBootComplete) {
        console.log('intercom first boot running...');
        // @ts-ignore:next-line
        window.Intercom('boot', {
          app_id: appId,
          name: `${userProfile?.first_name} ${userProfile?.last_name}`,
          email: `${userProfile?.email}`,
          phone: `+${userProfile?.country_code}${userProfile?.phone}`,
          api_base: 'https://api-iam.intercom.io',
          hide_default_launcher:
            // !accessToken?.sellerToken ||
            currentRoute === '/onboarding' ||
            currentRoute === '/login' ||
            currentRoute === '/forgot-password' ||
            currentRoute === '/reset-password' ||
            // currentRoute === '/register' ||
            currentRoute === '/',
        });

        dispatch(
          updateIntercom({
            isFirstBootComplete: true,
          })
        );
      } else {
        // Update launcher visibility based on token availability & currentRoute
        // @ts-ignore:next-line
        window.Intercom('update', {
          hide_default_launcher:
            // !accessToken?.sellerToken ||
            currentRoute === '/onboarding' ||
            currentRoute === '/login' ||
            currentRoute === '/forgot-password' ||
            currentRoute === '/reset-password' ||
            // currentRoute === '/register' ||
            currentRoute === '/',
        });
      }

      // Only update user data if user is logged in
      if (accessToken?.sellerToken) {
        // @ts-ignore:next-line
        window.intercomSettings = {
          email: `${userProfile.email}`,
          app_id: appId,
          cms: intercomData.cms,
          cmsdatafilled: intercomData.cmsdatafilled,
          psp: intercomData.psp,
          pspdatafilled: intercomData.pspdatafilled,
        };

        console.log('intercom updating...');
        // @ts-ignore:next-line
        window.Intercom('update', {
          app_id: appId,
          name: `${userProfile.first_name} ${userProfile.last_name}`,
          email: `${userProfile.email}`,
          api_base: 'https://api-iam.intercom.io',
          hide_default_launcher:
            currentRoute === '/onboarding' ||
            currentRoute === '/login' ||
            currentRoute === '/forgot-password' ||
            currentRoute === '/reset-password' ||
            // currentRoute === '/register' ||
            currentRoute === '/',
        });
      }
    }
    // @ts-ignore:next-line
    /* console.log(window.intercomSettings); */
  }, [
    APP_ENV,
    userProfile,
    accessToken?.sellerToken,
    currentRoute,
    intercomData,
    dispatch,
  ]);
  /* -------------run intercom when loggedin user data loads code end--------------- */

  const logoutHandler = () => {
    tokenRevokeAtLogOutUser();
    if (INTERCOM_ENABLED_ENVS.includes(APP_ENV)) {
      // @ts-ignore:next-line
      window.Intercom && window.Intercom('shutdown'); //shutdown intercom
    }

    localStorageHardReset();

    if (isWhiteLabel) {
      navigate('/authorize');
    } else {
      //* Explicitly handle base URL case to prevent getting stuck
      const currentPath = window?.location?.pathname;
      if (currentPath === '/' || currentPath === '/register') {
        navigate(currentPath === '/register' ? '/register' : '/login', {
          replace: true,
        });
      } else {
        navigate('/login', { replace: true });
      }
      fetchAppInit();
    }
  };

  const renderHeader = () => {
    if (window?.location?.pathname === '/onboarding') {
      return null;
    }
    // Phase 6-C — Client/Admin portals have their own chrome; don't render
    // the legacy seller header on top of them.
    if (
      window?.location?.pathname?.startsWith('/client-portal') ||
      window?.location?.pathname?.startsWith('/admin')
    ) {
      return null;
    }
    if (accessToken?.sellerToken || isNotEmptyString(cacheBustToken)) {
      if (isInitSuccess && !isOnboardingCompleteBannerOpen && !appLoading) {
        return (
          <HeaderMain
            isSidebarDrawerOpen={isSidebarDrawerOpen}
            toggleSidebarDrawer={toggleSidebarDrawer}
          />
        );
      }
    }

    return null;
  };

  /*   --------------------- Tara Chat Animation Start --------------------- */
  const [hoveredNearHeader, setHoveredNearHeader] = useState(false);

  // Desktop watcher (≥ 801px)
  const [isDesktop, setIsDesktop] = useState<boolean>(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(min-width: 801px)').matches
      : true
  );

  // ---- keep latest Redux values in refs (fix stale closures) ----
  const isSidebarMinimizedRef = useRef(isSidebarMinimized);
  const isChatAnimTriggeredRef = useRef(isChatAnimTriggered);
  useEffect(() => {
    isSidebarMinimizedRef.current = isSidebarMinimized;
  }, [isSidebarMinimized]);
  useEffect(() => {
    isChatAnimTriggeredRef.current = isChatAnimTriggered;
  }, [isChatAnimTriggered]);

  // ---- media query effect (TS-safe, legacy-friendly) ----
  useEffect(() => {
    if (typeof window === 'undefined' || !('matchMedia' in window)) return;
    const mql = window.matchMedia('(min-width: 801px)');

    setIsDesktop(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);

    if (typeof mql.addEventListener === 'function')
      mql.addEventListener('change', onChange);
    else (mql as any).addListener(onChange);

    return () => {
      if (typeof mql.removeEventListener === 'function')
        mql.removeEventListener('change', onChange);
      else (mql as any).removeListener(onChange);
    };
  }, []);

  // ---- main animation effect ----
  useEffect(() => {
    // Route gates
    const isSearchRoute = currentRoute.startsWith('/tara/search');
    const isChatRoute = currentRoute.startsWith('/tara');

    const removeStrip = () => {
      const stale = document.getElementById('tara-hover-reveal-strip');
      if (stale?.parentNode) stale.parentNode.removeChild(stale);
    };

    // If not on /tara/chat OR on /tara/search OR not desktop OR feature disabled:
    // force INITIAL, clean up, and clear the trigger
    if (
      !isChatRoute ||
      isSearchRoute ||
      !isDesktop ||
      !isTaraChatAnimationEnabled
    ) {
      taraChatAnimationEngine('initial', isSidebarMinimizedRef.current);
      setHoveredNearHeader(false);
      dispatch(setIsChatAnimTriggered(false));
      removeStrip();
      return;
    }

    // If the animation hasn't been triggered yet, stay INITIAL (no compact, no hover logic)
    if (!isChatAnimTriggeredRef.current) {
      taraChatAnimationEngine('initial', isSidebarMinimizedRef.current);
      setHoveredNearHeader(false);
      removeStrip();
      return;
    }

    // --- Desktop + /tara/chat + feature enabled + TRIGGERED ---

    // Baseline: compact after a short delay
    // Use actual state value instead of ref to ensure correct sidebar state
    const baselineTimer = window.setTimeout(() => {
      taraChatAnimationEngine('compact', isSidebarMinimized);
    }, 500);

    // Ensure a hover strip exists (top of viewport)
    let strip = document.getElementById(
      'tara-hover-reveal-strip'
    ) as HTMLElement | null;
    let createdStrip = false;
    if (!strip) {
      strip = document.createElement('div');
      strip.id = 'tara-hover-reveal-strip';
      Object.assign(strip.style, {
        position: 'fixed',
        top: '0px',
        left: '0px',
        right: '0px',
        height: '32px',
        zIndex: '1', // BELOW header (header is 2)
        pointerEvents: 'auto',
      });
      document.body.appendChild(strip);
      createdStrip = true;
    }

    // Header listeners: attach when header exists (covers mount-from-other-route case)
    let boundHeader: HTMLElement | null = null;
    let headerObserver: MutationObserver | null = null;

    const disableStrip = () => {
      if (strip) strip.style.pointerEvents = 'none';
    };
    const enableStrip = () => {
      if (strip) strip.style.pointerEvents = 'auto';
    };

    const onHeaderEnter = () => {
      cancelCompact();
      isHoveringHeader = true;
      setHoveredNearHeader(true);
      taraChatAnimationEngine('headerOnly', isSidebarMinimizedRef.current);
      // strip stays disabled while header is visible
      disableStrip();
    };
    const onHeaderLeave = () => {
      isHoveringHeader = false;
      enableStrip();
      scheduleCompact();
    };

    const bindHeader = (hdr: HTMLElement) => {
      if (boundHeader) return; // already bound
      boundHeader = hdr;
      hdr.addEventListener('mouseenter', onHeaderEnter);
      hdr.addEventListener('mouseleave', onHeaderLeave);
    };

    const tryBindHeaderNow = () => {
      const hdr = document.getElementById(
        'tara-header-bar'
      ) as HTMLElement | null;
      if (hdr) bindHeader(hdr);
      return !!hdr;
    };

    if (!tryBindHeaderNow()) {
      // Watch DOM for header mount, then bind once
      headerObserver = new MutationObserver(() => {
        if (tryBindHeaderNow()) {
          headerObserver?.disconnect();
          headerObserver = null;
        }
      });
      headerObserver.observe(document.body, { childList: true, subtree: true });
    }

    // Hover strip behavior
    let leaveTimer: number | null = null;
    let isHoveringHeader = false;

    const scheduleCompact = () => {
      if (leaveTimer) window.clearTimeout(leaveTimer);
      leaveTimer = window.setTimeout(() => {
        if (!isHoveringHeader) {
          setHoveredNearHeader(false);
          taraChatAnimationEngine('compact', isSidebarMinimizedRef.current);
          // Re-enable strip when compacting
          if (boundHeader) enableStrip();
        }
      }, 200) as unknown as number;
    };
    const cancelCompact = () => {
      if (leaveTimer) {
        window.clearTimeout(leaveTimer);
        leaveTimer = null;
      }
    };

    const onStripEnter = () => {
      cancelCompact();
      setHoveredNearHeader(true);
      taraChatAnimationEngine('headerOnly', isSidebarMinimizedRef.current);
    };
    const onStripLeave = () => {
      if (!isHoveringHeader) {
        scheduleCompact();
      }
    };

    strip.addEventListener('mouseenter', onStripEnter);
    strip.addEventListener('mouseleave', onStripLeave);

    const onScroll = () => {
      if (window.scrollY <= 2) onStripEnter();
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.clearTimeout(baselineTimer);
      strip?.removeEventListener('mouseenter', onStripEnter);
      strip?.removeEventListener('mouseleave', onStripLeave);
      if (boundHeader) {
        boundHeader.removeEventListener('mouseenter', onHeaderEnter);
        boundHeader.removeEventListener('mouseleave', onHeaderLeave);
      }
      if (headerObserver) {
        headerObserver.disconnect();
        headerObserver = null;
      }
      window.removeEventListener('scroll', onScroll);
      if (createdStrip && strip?.parentNode)
        strip.parentNode.removeChild(strip);
      if (leaveTimer) window.clearTimeout(leaveTimer);
    };
    // isSidebarMinimized is intentionally NOT a dependency here: this effect
    // reads it via isSidebarMinimizedRef.current for the animation only. It must
    // never WRITE the flag: the chat's compact/hover-reveal layout is driven
    // entirely by taraChatAnimationEngine (DOM CSS variables), and 'initial'
    // fully restores that layout regardless of the flag's value. The only
    // legitimate writer of isSidebarMinimized is the user's manual sidebar
    // toggle, so the early-return branches leave the flag untouched — otherwise
    // any route change (including plain non-/tara -> non-/tara navigation, which
    // re-runs this effect via currentRoute) would clobber a manual collapse and
    // re-expand the sidebar. The "keep compact layout in sync" effect below owns
    // the reactive path for isSidebarMinimized on the chat route.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentRoute, // re-run on route change
    isDesktop,
    isTaraChatAnimationEnabled,
    isChatAnimTriggered, // re-run when submit flips it to true
    dispatch,
  ]);

  // ---- keep compact layout in sync when the user toggles the sidebar ----
  useEffect(() => {
    const isSearchRoute = currentRoute.startsWith('/tara/search');
    const isChatRoute = currentRoute.startsWith('/tara');
    if (
      !isChatRoute ||
      isSearchRoute ||
      !isDesktop ||
      !isTaraChatAnimationEnabled
    )
      return;
    if (!isChatAnimTriggered) return;

    if (hoveredNearHeader) {
      taraChatAnimationEngine('headerOnly', isSidebarMinimized);
    } else {
      taraChatAnimationEngine('compact', isSidebarMinimized);
    }
  }, [
    isSidebarMinimized,
    isChatAnimTriggered,
    isDesktop,
    isTaraChatAnimationEnabled,
    currentRoute,
    hoveredNearHeader,
  ]);

  /*   --------------------- Tara Chat Animation End --------------------- */

  //* For better loading animation and prevent flickering
  //*
  //* Hotfix — the splash used to hang until `window.load` fired, with no upper
  //* bound. On a cold visit to a public auth route (/register, /login) the page
  //* pulls in third-party tags (Google GSI, Clarity, TikTok pixel, GA); if any
  //* of those stalls or is blocked, `load` never fires and the splash stayed on
  //* screen indefinitely. A reload "fixed" it only because those resources were
  //* then cached. The load event is now a fast path, never a dependency: a
  //* watchdog always clears the splash.
  useEffect(() => {
    const timers: number[] = [];
    const clearSplashIn = (delay: number) => {
      timers.push(window.setTimeout(() => setAppLoading(false), delay));
    };

    if (document.readyState === 'complete') {
      clearSplashIn(APP_LOADING_FLICKER_GUARD_MS);
    } else {
      const onPageLoad = () => clearSplashIn(APP_LOADING_FLICKER_GUARD_MS);
      window.addEventListener('load', onPageLoad, false);
      //? Upper bound — render the app even if `load` never arrives.
      clearSplashIn(APP_LOADING_MAX_WAIT_MS);
      return () => {
        window.removeEventListener('load', onPageLoad);
        timers.forEach((timer) => window.clearTimeout(timer));
      };
    }

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, []);

  return (
    <div
      style={{
        backgroundColor:
          window.location.pathname === '/tara' ||
          window.location.pathname.startsWith('/tara/chat/')
            ? 'var(--sidebar-background-color)'
            : 'var(--main-background-color)',
      }}
      data-is-white-label={isWhiteLabel?.toString()}
      className='App'
    >
      {/* Onboarding Complete Banner */}
      <AnimatePresence>
        {message.messageType && !isPlanLockToast && (
          <Message msgType={message.messageType} msg={message.message} />
        )}
        {isOnboardingCompleteBannerOpen && <OnboardingCompleteBanner />}
      </AnimatePresence>

      {/* Pricing Plans Popup */}
      {isInitSuccess && !isOnboardingCompleteBannerOpen && !appLoading && (
        <div className='recommendations-payment-popup-custom'>
          <Popup
            styles={{
              backgroundColor: 'var(--sidebar-background-color)',
              width: showChangePlanView ? '90%' : undefined,
              maxWidth: showChangePlanView ? '1200px' : undefined,
              maxHeight: showChangePlanView ? '90vh' : undefined,
              overflow: showChangePlanView ? 'auto' : undefined,
            }}
            setTrigger={togglePaymentPopupModal}
            trigger={isPaymentPopupOpen}
          >
            {showChangePlanView && taraSubscriptionDetails ? (
              <div style={{ padding: '1rem' }}>
                <div
                  className='agencyPackages'
                  style={{
                    justifyItems: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                  }}
                >
                  <div
                    className='agencyPackages__items'
                    style={{
                      flexWrap: 'wrap',
                      justifyContent: 'space-evenly',
                    }}
                  >
                    {(() => {
                      const rolePackages =
                        availableTaraSubscriptionPackages?.structuredPackageData?.filter(
                          (pkg: any) => pkg.role === 'agency'
                        ) || [];

                      if (rolePackages.length === 0) return null;

                      // Only the agency per-seat plan is offered, whatever
                      // the organisation role. A legacy seller plan ranks
                      // below every agency plan.
                      const PackageItemComponent = AgencyPackageItem;
                      const pricingHierarchy = tara_agency_pricing_hierarchy;
                      const currentRank =
                        pricingHierarchy[
                          taraSubscriptionDetails?.package_slug as keyof typeof pricingHierarchy
                        ] ?? 0;

                      const activeItems: JSX.Element[] = [];
                      const upgradableItems: JSX.Element[] = [];
                      const disabledItems: JSX.Element[] = [];

                      rolePackages.forEach((pkg: any) => {
                        const monthlyIsActive =
                          taraSubscriptionDetails?.package_slug ===
                          pkg.monthly_slug;
                        const annualIsActive =
                          taraSubscriptionDetails?.package_slug ===
                          pkg.annual_slug;

                        const purchasedSeatCount =
                          (monthlyIsActive || annualIsActive) &&
                          taraSubscriptionDetails?.purchased_shops?.seat_count
                            ? parseInt(
                                taraSubscriptionDetails.purchased_shops
                                  .seat_count
                              )
                            : undefined;

                        const monthlyIsUpgradable =
                          currentRank <
                          pricingHierarchy[
                            pkg.monthly_slug as keyof typeof pricingHierarchy
                          ];

                        const annualIsUpgradable =
                          currentRank <
                          pricingHierarchy[
                            pkg.annual_slug as keyof typeof pricingHierarchy
                          ];

                        const monthlyItem = (
                          <PackageItemComponent
                            key={`${pkg.id}-monthly`}
                            isMonthly={true}
                            isCancelMode={true}
                            purchased_seat_count={purchasedSeatCount}
                            packageDetails={{
                              ...pkg,
                              is_active: monthlyIsActive,
                              subscription_id: monthlyIsActive
                                ? taraSubscriptionDetails?.id
                                : undefined,
                              next_billing_date: monthlyIsActive
                                ? taraSubscriptionDetails?.end_date
                                : undefined,
                              is_most_popular: false,
                            }}
                            isUpgradable={monthlyIsUpgradable}
                          />
                        );

                        const annualItem = (
                          <PackageItemComponent
                            key={`${pkg.id}-annual`}
                            isMonthly={false}
                            isCancelMode={true}
                            purchased_seat_count={purchasedSeatCount}
                            packageDetails={{
                              ...pkg,
                              is_active: annualIsActive,
                              subscription_id: annualIsActive
                                ? taraSubscriptionDetails?.id
                                : undefined,
                              next_billing_date: annualIsActive
                                ? taraSubscriptionDetails?.end_date
                                : undefined,
                              is_most_popular: false,
                            }}
                            isUpgradable={annualIsUpgradable}
                          />
                        );

                        if (monthlyIsActive) {
                          activeItems.push(monthlyItem);
                        } else if (monthlyIsUpgradable) {
                          upgradableItems.push(monthlyItem);
                        } else {
                          disabledItems.push(monthlyItem);
                        }

                        if (annualIsActive) {
                          activeItems.push(annualItem);
                        } else if (annualIsUpgradable) {
                          upgradableItems.push(annualItem);
                        } else {
                          disabledItems.push(annualItem);
                        }
                      });

                      return [
                        ...activeItems,
                        ...upgradableItems,
                        ...disabledItems,
                      ];
                    })()}
                  </div>
                </div>
              </div>
            ) : (
              <SubscriptionPackages
                taraSubscriptionPackages={taraSubscriptionPackages}
              />
            )}
          </Popup>
        </div>
      )}

      {/* Switch Shop Popup */}
      {isInitSuccess && !isOnboardingCompleteBannerOpen && !appLoading && (
        <div className='recommendations-payment-popup-custom'>
          <Popup
            styles={{
              backgroundColor: 'var(--sidebar-background-color)',
            }}
            setTrigger={toggleSwitchShopPopupModal}
            trigger={isSwitchShopPopupOpen}
            switching={isShopSwitching}
          >
            <SwitchShop onSwitchingChange={setIsShopSwitching} />
          </Popup>
        </div>
      )}

      {/* Help Setup Popup */}
      {/* Hidden for new test onboarding process */}
      {/*       {isInitSuccess && !isOnboardingCompleteBannerOpen && !appLoading && (
        <HelpSetupPopup
          isOpen={isHelpSetupPopupOpen}
          onClose={() => {
            dispatch(setHasSkippedHelpSetup(true));
            dispatch(setIsHelpSetupPopupOpen(false));
          }}
        />
      )} */}

      {/* Header */}
      {renderHeader()}

      {/* //* App Routes --- START */}
      <React.Fragment>
        {/* Phase B — show the splash while (a) the brief load-flicker guard is
            active, (b) the agency cookie-restore `/me` is in flight, or (c) a
            seller session is mid-init. Crucially NOT gated on `!isInitSuccess`
            alone: a tokenless cold reload must fall through to the route guards
            (→ /login) rather than hang on an init that never completes. */}
        {(appLoading ||
          agencySessionResolving ||
          (!isInitSuccess && hasSellerSession)) &&
        window?.location?.pathname !== '/authorize' &&
        window?.location?.pathname !== '/login-via-portal' &&
        !window?.location?.pathname?.startsWith('/agency') &&
        !window?.location?.pathname?.startsWith('/email-approve') &&
        !window?.location?.pathname?.startsWith('/client-portal') &&
        !window?.location?.pathname?.startsWith('/admin') &&
        !window?.location?.pathname?.startsWith('/demo') ? (
          <SplashScreenNew />
        ) : isRouteClosedByPlanLock ? (
          planLock.canManageBilling ? (
            <Navigate to={SUBSCRIPTION_PAGE_ROUTE} replace />
          ) : (
            <PlanLockScreen />
          )
        ) : (
          <Routes>
            <Route
              path='/'
              element={
                isWhiteLabel ? (
                  <Navigate to={'/authorize'} />
                ) : (
                  <Navigate to={'/login'} />
                )
              }
            />
            <Route
              path='/register'
              element={
                !accessToken?.sellerToken && !isWhiteLabel ? (
                  <GoogleOAuthProvider clientId={GOOGLE_OAUTH_CLIENT_ID}>
                    {/* {APP_ENV === 'DEV' ? <MainAuthWrapper /> : <SignUpPage />} */}
                    <MainAuthWrapper />
                  </GoogleOAuthProvider>
                ) : (
                  <Navigate to={'/onboarding'} />
                )
              }
            />
            <Route
              path='/login'
              element={
                !accessToken?.sellerToken && !isWhiteLabel ? (
                  <GoogleOAuthProvider clientId={GOOGLE_OAUTH_CLIENT_ID}>
                    {/* {APP_ENV === 'DEV' ? <MainAuthWrapper /> : <LoginPage />} */}
                    <MainAuthWrapper />
                  </GoogleOAuthProvider>
                ) : !isWhiteLabel ? (
                  <Navigate to={HOME_ROUTE} />
                ) : (
                  <Navigate to={'/authorize'} />
                )
              }
            />
            <Route
              path='/forgot-password'
              element={
                !accessToken?.sellerToken && !isWhiteLabel ? (
                  <GoogleOAuthProvider clientId={GOOGLE_OAUTH_CLIENT_ID}>
                    {/* {APP_ENV === 'DEV' ? (
                      <MainAuthWrapper />
                    ) : (
                      <ForgotPassword />
                    )} */}
                    <MainAuthWrapper />
                  </GoogleOAuthProvider>
                ) : !isWhiteLabel ? (
                  <Navigate to={HOME_ROUTE} />
                ) : (
                  <Navigate to={'/authorize'} />
                )
              }
            />
            <Route
              path='/reset-password'
              element={
                !accessToken?.sellerToken && !isWhiteLabel ? (
                  <GoogleOAuthProvider clientId={GOOGLE_OAUTH_CLIENT_ID}>
                    {/* {APP_ENV === 'DEV' ? (
                      <MainAuthWrapper />
                    ) : (
                      <ResetPassword />
                    )} */}
                    <MainAuthWrapper />
                  </GoogleOAuthProvider>
                ) : !isWhiteLabel ? (
                  <Navigate to={HOME_ROUTE} />
                ) : (
                  <Navigate to={'/authorize'} />
                )
              }
            />
            <Route
              path='/onboarding/*'
              element={
                accessToken?.sellerToken ||
                accessToken?.currentWhiteLabelSellerToken ||
                isNotEmptyString(cacheBustToken) ? (
                  <Onboarding />
                ) : !isWhiteLabel ? (
                  <Navigate to={'/login'} />
                ) : (
                  <Navigate to={'/authorize'} />
                )
              }
            />
            {allRoutes['/dashboard'] && (
              <Route
                path='/dashboard/'
                element={
                  accessToken?.sellerToken ||
                  isNotEmptyString(cacheBustToken) ||
                  isWhiteLabel ||
                  !!employeeIdentity ? (
                    // Show the dashboard once onboarding is done. Connect work
                    // (tracking script / platforms) now lives in the in-app
                    // dashboard checklist, so a freshly-onboarded user must NOT
                    // be bounced into /integrations just because the tracking
                    // script isn't wired yet. Keep the legacy tracking-script
                    // gate only as a fallback for sessions still mid-onboarding.
                    (isTrackingScriptConnectionSuccess &&
                      isTrackingScriptConnectionSuccess === 'true') ||
                    !is_ai_onboarding_inprogress ? (
                      <Dashboard statistics={statistics} />
                    ) : (
                      <Navigate to={'/integrations'} />
                    )
                  ) : (
                    <Navigate to={'/login'} />
                  )
                }
              />
            )}
            {allRoutes['/reports'] && (
              <Route
                path='/reports/*'
                element={
                  accessToken?.sellerToken ||
                  isNotEmptyString(cacheBustToken) ||
                  isWhiteLabel ||
                  !!employeeIdentity ? (
                    <Notifications />
                  ) : (
                    <Navigate to={'/login'} />
                  )
                }
              />
            )}
            {allRoutes['/ai-optimizations'] && (
              <Route
                path='/ai-optimizations/*'
                element={
                  accessToken?.sellerToken ||
                  isNotEmptyString(cacheBustToken) ||
                  isWhiteLabel ||
                  !!employeeIdentity ? (
                    <AIOptimizations />
                  ) : (
                    <Navigate to={'/login'} />
                  )
                }
              />
            )}
            {allRoutes['/actions'] && (
              <Route
                path='/actions/*'
                element={
                  accessToken?.sellerToken ||
                  isNotEmptyString(cacheBustToken) ||
                  isWhiteLabel ||
                  !!employeeIdentity ? (
                    <ActionsPage />
                  ) : (
                    <Navigate to={'/login'} />
                  )
                }
              />
            )}
            {allRoutes['/settings'] && (
              <Route
                path='/settings/*'
                element={
                  accessToken?.sellerToken ||
                  isNotEmptyString(cacheBustToken) ||
                  isWhiteLabel ||
                  !!employeeIdentity ? (
                    <SettingsPage />
                  ) : (
                    <Navigate to={'/login'} />
                  )
                }
              />
            )}
            {/* P3 — Client-report portal (agency review/edit/approve). The
                page re-gates on portal.reports.review; route visibility is
                DEV-first in route.ts. Nested: queue / :id workbench / clients. */}
            {allRoutes['/client-reports'] && (
              <Route
                path='/client-reports/*'
                element={
                  accessToken?.sellerToken ||
                  isNotEmptyString(cacheBustToken) ||
                  isWhiteLabel ||
                  !!employeeIdentity ? (
                    <ClientReportsPage />
                  ) : (
                    <Navigate to={'/login'} />
                  )
                }
              />
            )}
            {allRoutes['/clients'] && (
              <Route
                path='/clients/*'
                element={
                  accessToken?.sellerToken ||
                  isNotEmptyString(cacheBustToken) ||
                  isWhiteLabel ||
                  !!employeeIdentity ? (
                    <ClientsPage />
                  ) : (
                    <Navigate to={'/login'} />
                  )
                }
              />
            )}
            {/* The unified hub. The FULL Control Center is hosted at
                /integrations (the whitelisted OAuth redirect URL) with the
                Integrations tab open first. Its integrations tab renders the
                same MCP grid that reads the OAuth ?code/?state callback, so the
                round-trip keeps working. The old standalone Integrations page is
                retired; /control-center redirects here (see below). */}
            {allRoutes['/integrations'] && (
              <Route
                path='/integrations/*'
                element={
                  accessToken?.sellerToken ||
                  isNotEmptyString(cacheBustToken) ||
                  isWhiteLabel ||
                  !!employeeIdentity ? (
                    <ControlCenter />
                  ) : (
                    <Navigate to={'/login'} />
                  )
                }
              />
            )}
            {/* W3 — the Control Center now lives at /integrations. Keep
                /control-center as a redirect (preserving ?tab=) so existing
                deep-links such as ?tab=tool-access keep resolving. */}
            {allRoutes['/control-center'] && (
              <Route
                path='/control-center/*'
                element={
                  <Navigate
                    to={`/integrations${window.location.search}`}
                    replace
                  />
                }
              />
            )}
            {/* Agents hub (formerly the Skills hub). `/skills/*` stays as a
                permanent redirect so old bookmarks and deep links resolve. */}
            {allRoutes['/agents'] && (
              <Route
                path='/agents/*'
                element={
                  isNewDemoMode() ? (
                    // Demo 2.0 — a standalone fixture Agents page (lazy: stays
                    // out of the prod bundle).
                    <Suspense fallback={null}>
                      <DemoAgentsPage />
                    </Suspense>
                  ) : isDemoMode() ? (
                    // /demo — the same fixture page with that demo's own
                    // tracker and styles (lazy as well).
                    <Suspense fallback={null}>
                      <DemoClassicAgentsPage />
                    </Suspense>
                  ) : accessToken?.sellerToken ||
                    isNotEmptyString(cacheBustToken) ||
                    isWhiteLabel ||
                    !!employeeIdentity ? (
                    <Skills />
                  ) : (
                    <Navigate to={'/login'} />
                  )
                }
              />
            )}
            {allRoutes['/clients'] && (
              // The sidebar calls this page "Brands"; the list lives at /clients.
              <Route
                path='/brands/*'
                element={<Navigate to='/clients' replace />}
              />
            )}
            {allRoutes['/skills'] && (
              <Route path='/skills/*' element={<LegacySkillsRedirect />} />
            )}
            {allRoutes['/prompt-schedules'] && (
              <Route
                path='/prompt-schedules/*'
                element={
                  accessToken?.sellerToken ||
                  isNotEmptyString(cacheBustToken) ||
                  isWhiteLabel ||
                  !!employeeIdentity ? (
                    <PromptSchedules />
                  ) : (
                    <Navigate to={'/login'} />
                  )
                }
              />
            )}
            {/* Phase 13 — Team page (top-level; was nested under /settings).
                Registered BEFORE /my-mcp-setup so route ordering matches the
                sidebar's main-menu order. */}
            {allRoutes['/team'] && (
              <Route
                path='/team'
                element={
                  accessToken?.sellerToken ||
                  isNotEmptyString(cacheBustToken) ||
                  isWhiteLabel ||
                  !!employeeIdentity ? (
                    <TeamPage />
                  ) : (
                    <Navigate to={'/login'} />
                  )
                }
              />
            )}
            {/* MCP-client OAuth consent (TARA_PLUGIN_OAUTH2_PLAN.md WS3).
                Mounted without an auth gate on purpose: the page itself parks
                the request and sends a signed-out visitor to /login, and the
                return-to effect above brings them back. Gates on the agency
                identity, never on the seller token (plan D16). */}
            {allRoutes['/oauth/authorize'] && (
              <Route path='/oauth/authorize' element={<OauthConsent />} />
            )}
            {/* Phase 13 — per-employee Claude Code plugin setup page. */}
            {allRoutes['/my-mcp-setup'] && (
              <Route
                path='/my-mcp-setup'
                element={
                  accessToken?.sellerToken ||
                  isNotEmptyString(cacheBustToken) ||
                  isWhiteLabel ||
                  !!employeeIdentity ? (
                    <McpSetup />
                  ) : (
                    <Navigate to={'/login'} />
                  )
                }
              />
            )}
            {allRoutes['/tracking'] && (
              <Route
                path='/tracking/*'
                element={
                  accessToken?.sellerToken ||
                  isNotEmptyString(cacheBustToken) ||
                  isWhiteLabel ||
                  !!employeeIdentity ? (
                    <CheckoutTrackingPage />
                  ) : (
                    <Navigate to={'/login'} />
                  )
                }
              />
            )}
            {/* Phase 6-C — /recommendations route removed; pages/recommendations deleted. */}
            <Route
              path='/plugin-connect'
              element={
                accessToken?.sellerToken || isNotEmptyString(cacheBustToken) ? (
                  <PluginAuthPage />
                ) : !isWhiteLabel ? (
                  <Navigate to={'/login'} />
                ) : (
                  <Navigate to={'/authorize'} />
                )
              }
            />
            <Route
              path='/email-verification-success'
              element={<EmailVerificationSuccess />}
            />
            <Route path='/verify-email' element={<VerifyEmail />} />
            <Route path='/unsubscribe' element={<Unsubscribe />} />
            <Route
              path='/stripe-subscription-success'
              element={
                accessToken?.sellerToken || isNotEmptyString(cacheBustToken) ? (
                  <StripeSubscriptionSuccess />
                ) : !isWhiteLabel ? (
                  <Navigate to={'/login'} />
                ) : (
                  <Navigate to={'/authorize'} />
                )
              }
            />
            <Route
              path='/stripe-subscription-failed'
              element={
                accessToken?.sellerToken || isNotEmptyString(cacheBustToken) ? (
                  <StripeSubscriptionFailed />
                ) : !isWhiteLabel ? (
                  <Navigate to={'/login'} />
                ) : (
                  <Navigate to={'/authorize'} />
                )
              }
            />
            <Route
              path='/recommendations-subscription-status'
              element={
                accessToken?.sellerToken || isNotEmptyString(cacheBustToken) ? (
                  <RecommendationsSubscriptionStatus />
                ) : !isWhiteLabel ? (
                  <Navigate to={'/login'} />
                ) : (
                  <Navigate to={'/authorize'} />
                )
              }
            />
            <Route
              path='/aduppay/onboarding'
              element={
                accessToken?.sellerToken || isNotEmptyString(cacheBustToken) ? (
                  <AdUpPayOnboardingSuccess />
                ) : !isWhiteLabel ? (
                  <Navigate to={'/login'} />
                ) : (
                  <Navigate to={'/authorize'} />
                )
              }
            />
            <Route path='/authorize' element={<WhiteLabel />} />
            <Route path='/login-via-portal' element={<PortalLogin />} />
            {/* ---------- Phase 6-C — Client Portal (magic link) ---------- */}
            <Route
              path='/client-portal/auth'
              element={<ClientPortalMagicLink />}
            />
            {/* OTP login — the primary client sign-in (public, no auth wrapper,
                same treatment as the magic-link `/auth` route above). */}
            <Route
              path='/client-portal/login'
              element={<ClientPortalOtpLogin />}
            />
            <Route
              path='/client-portal/dashboard'
              element={
                <ClientPortalProtectedRoute>
                  <ClientPortalDashboard />
                </ClientPortalProtectedRoute>
              }
            />
            <Route
              path='/client-portal/reports'
              element={
                <ClientPortalProtectedRoute>
                  <ClientPortalReports />
                </ClientPortalProtectedRoute>
              }
            />
            <Route
              path='/client-portal/expired'
              element={<ClientPortalExpired />}
            />
            {/* The super-admin portal (/admin/*) was moved out of tara-client
                into the Adup Panel app. /admin now resolves to the 404 catch-all
                here. The admin API lives in central-api (/api/v1/admin/*). */}
            {/* ---------- Agency Stats deep-link survivor ----------
                The parallel `/agency/*` portal was retired in the FE
                consolidation wave. Only `/agency/stats` remains, since
                the legacy dashboard's "Detailed stats" header link
                points at it. No sidebar entry — deep-link only. The page
                gates internally on `state.auth.agency.employeeIdentity`
                so it's harmless to expose without a route-level wrapper. */}
            <Route path='/agency/stats' element={<AgencyStats />} />
            {/* Phase 13 — Stats also lives at the top-level `/stats`, surfaced
                in the main sidebar menu (between Dashboard and Brands). The
                legacy `/agency/stats` route remains for inbound deep-links. */}
            {allRoutes['/stats'] && (
              <Route path='/stats' element={<AgencyStats />} />
            )}
            {/* W5 — Audit-log viewer. Same auth wrapper pattern as `/stats`:
                a top-level route behind the app auth gate; the page gates
                internally on the `audit.view` capability (owner/team_lead)
                and renders a "no permission" message for other roles. */}
            {/* Legacy standalone Audit log route now redirects into the
                unified Log Center (Audit tab). Kept so old links/bookmarks
                still resolve. */}
            {allRoutes['/audit-log'] && (
              <Route
                path='/audit-log'
                element={<Navigate to='/logs?tab=audit' replace />}
              />
            )}
            {/* Legacy standalone Meta API activity route now redirects into the
                unified Log Center (Meta API tab). */}
            {allRoutes['/meta-activity'] && (
              <Route
                path='/meta-activity'
                element={<Navigate to='/logs?tab=meta' replace />}
              />
            )}
            {/* Unified Log Center — hosts the Audit + Meta API tabs. Top-level
                route behind the app auth gate; each tab is gated internally on
                its own capability (`audit.view` / `meta_activity.view`), and a
                user with neither sees a "no permission" message. */}
            {allRoutes['/logs'] && (
              <Route path='/logs' element={<LogCenter />} />
            )}
            {/* ---------- Phase 9-D — public email approve/deny landing ----------
                The signed HMAC token in `?token=…` IS the credential, so
                this route stays OUTSIDE any auth wrapper. If the user
                happens to be signed in (Phase 11-Bridge populates
                `state.auth.agency.accessToken`), the optional Bearer is
                used to promote the recorded actor to `user:agency:{id}`. */}
            <Route
              path='/email-approve/:id'
              element={<EmailApprovalLanding />}
            />
            {/* ---------- Interactive product-demo funnel (PUBLIC) ----------
                Marketing/sales funnel at /demo. No auth wrapper, fully mocked
                (no OAuth/PII). Lazy-loaded to keep it out of the main bundle.
                The splash / fetchAppInit / token-expiry effects above skip any
                path starting with /demo so it renders bare (build brief §8). */}
            <Route
              path='/demo'
              element={
                <Suspense
                  fallback={
                    <div className='demo demo--loading'>
                      <span
                        className='demo__loader'
                        aria-label='Loading demo'
                      />
                    </div>
                  }
                >
                  <DemoLauncher />
                </Suspense>
              }
            />
            {/* Demo 2.0 — the NEW demo entry (same bare treatment as /demo). */}
            <Route
              path='/demo-new'
              element={
                <Suspense
                  fallback={
                    <div className='demo demo--loading'>
                      <span
                        className='demo__loader'
                        aria-label='Loading demo'
                      />
                    </div>
                  }
                >
                  <DemoNewLauncher />
                </Suspense>
              }
            />
            {/* Public client one-pager — renders bare, no auth, no backend. */}
            <Route
              path='/one-pager'
              element={
                <Suspense fallback={null}>
                  <OnePager />
                </Suspense>
              }
            />
            {/* Signs out exactly like "Sign Out" in the profile menu. */}
            <Route path='/logout' element={<LogoutRoute />} />
            <Route path='*' element={<PageNotFound />} />
          </Routes>
        )}
        {/* Guided-tour overlay + demo control bar — only in demo mode, and not
            on the /demo launcher itself. */}
        {isDemoMode() && currentRoute !== '/demo' && (
          <Suspense fallback={null}>
            <DemoShell />
          </Suspense>
        )}
        {isNewDemoMode() && currentRoute !== '/demo-new' && (
          <Suspense fallback={null}>
            <DemoNewShell />
          </Suspense>
        )}
      </React.Fragment>
    </div>
  );
}

export default App;
